import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import classNames from 'classnames';

import { Colours, Sizes, Typography, Paragraph } from '@ratehub/base-ui';
import {
    PRODUCT_TYPES,
    CardEarningTable,
    CardFeeSummaryLowInterest,
    CardFeeSummaryBalanceTransfer,
    CardCalculationModalBalanceTransfer,
} from '@ratehub/cc-common';
import { FormattedMessage } from 'react-intl';


function SummaryDetailsSelector({ cardInfo, ...otherProps }) {
    const productType = cardInfo.productType;

    const [ showCardCalculations, setShowCardCalculations ] = useState(false);


    function handleToggleShowCalculations() {
        setShowCardCalculations(!showCardCalculations);
    }

    return (
        <Container {...otherProps}>
            <Choose>
                <When condition={
                    productType === PRODUCT_TYPES.TRAVEL
                    || productType === PRODUCT_TYPES.CASH_BACK
                    || productType === PRODUCT_TYPES.STORE_CREDIT
                }
                >
                    <CardEarningTable
                        cardInfo={cardInfo}
                        subTitleSize="large"
                        subTitle={(
                            <FormattedMessage
                                id="cc.details.earning-table-subtitle"
                                defaultMessage="{productType, select,
                                    travel {Earn rewards}
                                    other {Earning categories}
                                }"
                                values={{ productType: productType }}
                            />
                        )}
                        productType={productType}
                    />
                </When>

                <When condition={productType === PRODUCT_TYPES.LOW_INTEREST
                    || productType === PRODUCT_TYPES.SECURED}
                >
                    <h3 className={classNames('rh-title-s', 'rh-mb-0_75')}>
                        <FormattedMessage
                            id="cc.details.card-details-title"
                            defaultMessage="Card details"
                        />
                    </h3>

                    <MyHR />

                    <CardFeeSummaryLowInterest
                        cardInfo={cardInfo}
                    />
                </When>

                <When condition={productType === PRODUCT_TYPES.BALANCE_TRANSFER}>
                    <h3 className={classNames('rh-title-s', 'rh-mb-0_75')}>
                        <FormattedMessage
                            id="cc.details.fee-summary-bt-subtitle"
                            defaultMessage="Balance transfer details"
                        />
                    </h3>

                    <MyParagraph
                        message={(
                            <FormattedMessage
                                id="cc.details.fee-summary-bt-note"
                                defaultMessage="Calculations based on transferred balance of <strong>{balanceTransferAmount, number, CAD0}</strong>"
                                values={cardInfo}
                            />
                        )}
                        size="small"
                    />

                    <MyHR />

                    <CardFeeSummaryBalanceTransfer
                        cardInfo={cardInfo}
                        onClick={handleToggleShowCalculations}
                    />

                    <CardCalculationModalBalanceTransfer
                        cardInfo={cardInfo}
                        isOpen={showCardCalculations}
                        onClose={handleToggleShowCalculations}
                    />
                </When>
            </Choose>
        </Container>
    );
}

SummaryDetailsSelector.propTypes = {
    cardInfo: PropTypes.shape({
        productType: PropTypes.string.isRequired,

        // Travel and Cash Back
        earningsDescription: PropTypes.arrayOf(PropTypes.shape({
            rate: PropTypes.number.isRequired,
            types: PropTypes.arrayOf(PropTypes.string).isRequired,
        })),

        // Low Interest
        annualFee: PropTypes.number,
        monthlyInterestCharge: PropTypes.number,
        lowInterestMonthlyBalance: PropTypes.number,

        // Balance Transfer
        balanceTransferPromoMonths: PropTypes.number,
        balanceTransferPromoInterest: PropTypes.number,
        balanceTransferFeeRate: PropTypes.number,
        balanceTransferFee: PropTypes.number,
    }).isRequired,
};


const Container = styled.div``;

const MyParagraph = styled(Paragraph)`
    font-size: ${Sizes.FONTS.S};
    margin-top: -${Sizes.SPACING.HALF};

    > strong {
        font-weight: ${Typography.WEIGHTS.MEDIUM};
    }
`;

const MyHR = styled.hr`
    width: 100%;
    border: none;
    border-top: 1px solid ${Colours.STONE};
    margin: ${Sizes.SPACING.THREE_QUARTERS} 0;
`;

export default SummaryDetailsSelector;
